import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

/** User profile returned by auth/me endpoint */
export const AuthMeUserSchema = z
  .object({
    user_id: z.number(),
    verified: z.boolean(),
    email: z.string().email(),
    role: z.string(),
    full_name: z.string(),
    customer_id: z.number(),
    date_added: z.string(),
    contact_phone_number: z.string().nullable(),
    contact_phone_country_code: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

/** Customer data returned by auth/me endpoint */
export const AuthMeCustomerSchema = z
  .object({
    customer_id: z.number(),
    name: z.string(),
    website_url: z.string().nullable(),
    country: z.string().nullable(),
    date_added: z.string(),
    business_type: z.string().nullable(),
    managed_location_range: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

/**
 * Subscription data returned by auth/me endpoint. A user without an active subscription still
 * gets the object, with the subscription's own identity and status fields nulled out.
 */
export const AuthMeSubscriptionSchema = z
  .object({
    id: z.number().nullable(),
    subscription_id: z.number().nullable(),
    package_id: z.string().nullable(),
    subscription_name: z.string().nullable(),
    status: z.string().nullable(),
    active_locations: z.number(),
    allowed_locations: z.number(),
    has_location_based_subscription: z.boolean()
  })
  .transform((data) => camelcaseKeys(data));

/** Feature allowance flags returned by auth/me endpoint */
export const FeatureAllowanceSchema = z.record(z.string(), z.union([z.boolean(), z.string()]));

/** Sysadmin identity when the current session is a Login As impersonation. */
export const AuthMeImpersonationSchema = z
  .object({
    impersonator_user_id: z.number(),
    impersonator_customer_id: z.number(),
    impersonator_user_uuid: z.string().uuid()
  })
  .transform((data) => camelcaseKeys(data));

/** Full response schema for auth/me endpoint */
export const AuthMeResponseSchema = z
  .object({
    user: AuthMeUserSchema,
    customer: AuthMeCustomerSchema,
    subscription: AuthMeSubscriptionSchema,
    feature_allowance: FeatureAllowanceSchema,
    impersonation: AuthMeImpersonationSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AuthMeResponse = z.infer<typeof AuthMeResponseSchema>;

/**
 * Request params for updating the signed-in user's settings; send only the fields that changed.
 * At least one field must be sent: the API answers an empty body with a 400. An empty string
 * clears the field it is sent for.
 */
export const UpdateUserSettingsParamsSchema = z.object({
  /** The user's full name; the API stores one field */
  fullName: z.string().optional(),
  /** Contact phone number */
  contactPhoneNumber: z.string().optional(),
  /** ISO 3166-1 alpha-3 code of the phone number's country, e.g. "USA" */
  contactPhoneCountryCode: z.string().optional()
});

/** Request params for updating user settings */
export type UpdateUserSettingsParams = z.infer<typeof UpdateUserSettingsParamsSchema>;

/** Success body of the update user settings endpoint: a nullable object in the contract, `null` in practice. */
export const UpdateUserSettingsResponseSchema = z.object({}).nullable();

/** Request params for changing the signed-in user's own password. */
export const ChangePasswordParamsSchema = z.object({
  /** The password the user signs in with now; the API checks it before changing anything */
  currentPassword: z.string(),
  /** The password to set: 12 to 50 characters, no character 12 times in a row, different from the current one */
  newPassword: z.string()
});

/** Request params for changing the signed-in user's own password */
export type ChangePasswordParams = z.infer<typeof ChangePasswordParamsSchema>;

/** The signed-in user's two-factor authentication state. */
export const TwoFactorStatusResponseSchema = z
  .object({
    /** `true` when 2FA is on */
    enabled: z.boolean(),
    /**
     * When the authenticator secret was created, RFC 3339 UTC; `null` until the first set-up. The secret
     * is kept when 2FA is turned off, so this stays set while `enabled` is `false`
     */
    secret_created_at: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

/** The signed-in user's two-factor authentication state */
export type TwoFactorStatusResponse = z.infer<typeof TwoFactorStatusResponseSchema>;

/**
 * What the app needs to show the 2FA QR code. Repeat set-ups while 2FA is off return the same
 * link, so a code the user already scanned stays valid.
 */
export const TwoFactorSetupResponseSchema = z
  .object({
    /** The `otpauth://` link to show as a QR code; it carries the secret the user can type in instead */
    otpauth_uri: z.string(),
    /** When the authenticator secret was created, RFC 3339 UTC; `null` for a secret saved without that date */
    secret_created_at: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

/** What the app needs to show the 2FA QR code */
export type TwoFactorSetupResponse = z.infer<typeof TwoFactorSetupResponseSchema>;

/** Request params for turning 2FA on. */
export const EnableTwoFactorParamsSchema = z.object({
  /** The current six-digit code from the authenticator app, leading zeros kept; backup codes are refused */
  code: z.string()
});

/** Request params for turning 2FA on */
export type EnableTwoFactorParams = z.infer<typeof EnableTwoFactorParamsSchema>;

/** The backup codes the API returns once, when 2FA is turned on. */
export const TwoFactorBackupCodesResponseSchema = z
  .object({
    /** 10 single-use codes of six letters or digits; this is the only time they are shown */
    backup_codes: z.array(z.string())
  })
  .transform((data) => camelcaseKeys(data));

/** The backup codes the API returns once, when 2FA is turned on */
export type TwoFactorBackupCodesResponse = z.infer<typeof TwoFactorBackupCodesResponseSchema>;

/** Request params for turning 2FA off. */
export const DisableTwoFactorParamsSchema = z.object({
  /** A current six-digit code from the authenticator app, or one of the user's unused six-character backup codes */
  code: z.string()
});

/** Request params for turning 2FA off */
export type DisableTwoFactorParams = z.infer<typeof DisableTwoFactorParamsSchema>;
